// src/components/assistant/agentConfig.ts
/**
 * Shared agent configuration for the AI assistant.
 * Used by ResearchWorkspace, AssistantChat, and status text helpers.
 */

import type { AgentId, MultiAgentStreamState } from "@/types/assistant";

// ─────────────────────────────────────────────────────────────────────────────
// Agent Configuration
// ─────────────────────────────────────────────────────────────────────────────

export interface AgentDisplayConfig {
  colorKey: string;
  glowColor: string;
  label: string;
  description: string;
  workingStatus: string;
}

export const AGENT_DISPLAY_CONFIG: Record<AgentId, AgentDisplayConfig> = {
  data: {
    colorKey: "emerald",
    glowColor: "rgb(16, 185, 129)",
    label: "Data Agent",
    description: "Fetching market data & metrics",
    workingStatus: "Querying database...",
  },
  search: {
    colorKey: "cyan",
    glowColor: "rgb(34, 211, 238)",
    label: "Search Agent",
    description: "Searching the web",
    workingStatus: "Searching the web...",
  },
  chart: {
    colorKey: "amber",
    glowColor: "rgb(245, 158, 11)",
    label: "Chart Agent",
    description: "Generating visualizations",
    workingStatus: "Generating visualizations...",
  },
  synthesizer: {
    colorKey: "violet",
    glowColor: "rgb(139, 92, 246)",
    label: "Synthesizer",
    description: "Compiling final report",
    workingStatus: "Composing response...",
  },
  quick_chat: {
    colorKey: "amber",
    glowColor: "rgb(245, 158, 11)",
    label: "Quick Chat",
    description: "Fast response with web search",
    workingStatus: "Generating response...",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Status Text Helper
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derive streaming status text from multiAgentState.
 * Returns a human-readable description of what the assistant is currently doing.
 */
export function getStreamingStatusText(multiAgentState: MultiAgentStreamState | undefined): string {
  if (!multiAgentState) return "Thinking...";
  
  // Check all agents for active tools first (most specific)
  for (const agent of Object.values(multiAgentState.agents)) {
    if (!agent) continue;
    
    // Check active tools - these give the most specific status
    for (const tool of agent.activeTools.values()) {
      const toolName = tool.name?.toLowerCase() ?? "";
      if (toolName.includes("web_search") || toolName.includes("search")) {
        return "Searching the web...";
      }
      if (toolName.includes("fetch") || toolName.includes("data") || toolName.includes("get")) {
        return "Querying database...";
      }
      if (toolName.includes("chart") || toolName.includes("plot")) {
        return "Generating chart...";
      }
      // Generic tool running
      return `Running ${tool.name}...`;
    }
    
    // Check agent status using shared config
    if (agent.status === "working") {
      const config = AGENT_DISPLAY_CONFIG[agent.id];
      if (config) {
        return config.workingStatus;
      }
    }
  }
  
  return "Thinking...";
}

